import { Platform } from 'react-native';

import { type ActionArgs, assertEvent, assign } from 'xstate5';

import { type Context, type Event, TargetMeasurement } from '../types';

// ─────────────────────────────────────────────────────────────────────────────

const updateActiveTarget: ScrollspyAction = assign({
  activeTargetId({ context }) {
    const { measurements, scrollOffset } = context;
    const scrollY = scrollOffset.value;

    const measurementEntries = Object.entries(measurements);
    const targets = measurementEntries.map(([id, measurement]) => ({ id, ...measurement }));
    const sortedTargets = targets.sort((target, nextTarget) => nextTarget.y - target.y);
    const activeTarget = sortedTargets.find((target) => scrollY >= target.y);

    return activeTarget?.id ?? sortedTargets.at(-1)?.id ?? context.activeTargetId;
  },
});

const setActiveTarget: ScrollspyAction = assign({
  activeTargetId({ event }) {
    assertEvent(event, 'target.scrollTo');

    return event.targetId;
  },
  programmaticTargetY({ context, event }) {
    assertEvent(event, 'target.scrollTo');

    return context.measurements[event.targetId]?.y ?? null;
  },
});

const scrollToTarget: ScrollspyAction = ({ context, event }) => {
  assertEvent(event, 'target.scrollTo');

  const measurement = context.measurements[event.targetId];

  if (!measurement) return;

  context.scrollViewRef.current?.scrollTo({ y: measurement.y, animated: true });
};

const reScrollToActiveTarget: ScrollspyAction = ({ context }) => {
  const { activeTargetId, measurements, scrollOffset, scrollViewRef, scrollViewDimensions } =
    context;

  if (!activeTargetId) return;

  const measurement = measurements[activeTargetId];

  if (!measurement) return;

  const scrollY = scrollOffset.value;
  const viewportHeight = scrollViewDimensions.viewportHeight;
  const targetTop = measurement.y;
  const targetBottom = measurement.y + measurement.height;
  const isWithinBounds = targetTop <= scrollY && targetBottom > scrollY + viewportHeight * 0.5;

  if (isWithinBounds) return;

  scrollViewRef.current?.scrollTo({ y: targetTop, animated: true });
};

const clearProgrammaticTarget: ScrollspyAction = assign({
  programmaticTargetY: () => null,
});

const storeMeasurement: ScrollspyAction = assign({
  measurements({ context, event }) {
    assertEvent(event, 'target.measure');

    const { y, height } = event.measurement;

    // NOTE: On web, `measureLayout` returns viewport-relative `Y` (negative when scrolled past).
    //       On native, it returns content-relative `Y` directly.
    const contentY = Platform.select({
      web: y + context.scrollOffset.value,
      default: y,
    });

    return { ...context.measurements, [event.targetId]: { y: contentY, height } };
  },
  targetRefs({ context, event }) {
    assertEvent(event, 'target.measure');

    return { ...context.targetRefs, [event.targetId]: event.targetRef };
  },
});

const registerTarget: ScrollspyAction = assign({
  targetRefs({ context, event }) {
    assertEvent(event, 'target.register');

    return { ...context.targetRefs, [event.targetId]: event.targetRef };
  },
});

const removeMeasurement: ScrollspyAction = assign({
  measurements({ context, event }) {
    assertEvent(event, 'target.deregister');

    const { [event.targetId]: _, ...rest } = context.measurements;

    return rest;
  },
  targetRefs({ context, event }) {
    assertEvent(event, 'target.deregister');

    const { [event.targetId]: _, ...rest } = context.targetRefs;

    return rest;
  },
});

const storeContentHeight: ScrollspyAction = assign({
  scrollViewDimensions({ context, event }) {
    assertEvent(event, 'scrollView.contentSizeChange');

    return { ...context.scrollViewDimensions, contentHeight: event.height };
  },
});

const storeViewportHeight: ScrollspyAction = assign({
  scrollViewDimensions({ context, event }) {
    assertEvent(event, 'scrollView.layout');

    return { ...context.scrollViewDimensions, viewportHeight: event.height };
  },
});

const storeAllMeasurements: ScrollspyAction<{ measurements: Record<string, TargetMeasurement> }> =
  assign({
    measurements(_, params) {
      return params.measurements;
    },
  });

// ─────────────────────────────────────────────────────────────────────────────

export const Actions = {
  updateActiveTarget,
  setActiveTarget,
  scrollToTarget,
  reScrollToActiveTarget,
  clearProgrammaticTarget,
  registerTarget,
  storeMeasurement,
  removeMeasurement,
  storeContentHeight,
  storeViewportHeight,
  storeAllMeasurements,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ScrollspyAction<ActionParams extends Record<string, unknown> | void = void> = (
  actionArgs: ActionArgs<Context, Event, Event>,
  params: ActionParams,
) => void;
